import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  Table,
  Badge,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaDownload,
  FaUserGraduate,
  FaEye,
  FaCalendarAlt,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '../../../components/common/Header';
import AttendanceService from '../../../services/attendanceService';
import StudentHistoryModal from './components/StudentHistoryModal';

const AttendanceHistory = () => {
  const navigate = useNavigate();
  const [selectedBatch, setSelectedBatch] = useState('');
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {
    try {
      setLoadingBatches(true);
      const response = await AttendanceService.getBatches();
      if (response.success) {
        setBatches(response.data);
      }
    } catch (error) {
      console.error('Failed to load batches:', error);
      toast.error('Failed to load batches');
    } finally {
      setLoadingBatches(false);
    }
  };

  const loadStudents = async (batchId) => {
    if (!batchId) return;
    
    try {
      setLoading(true);
      const response = await AttendanceService.getBatchStudents(batchId);
      if (response.success) {
        setStudents(response.data);
      }
    } catch (error) {
      toast.error('Failed to load students');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBatchChange = (e) => {
    const batchId = e.target.value;
    setSelectedBatch(batchId);
    if (batchId) {
      loadStudents(batchId);
    } else {
      setStudents([]);
    }
  };

  const handleStudentClick = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
  };

  const handleExport = async () => {
    if (!selectedBatch) {
      toast.error('Please select a batch first');
      return;
    }

    try {
      await AttendanceService.exportAttendance(selectedBatch);
      toast.success('Export started! Check your downloads folder.');
    } catch (error) {
      toast.error('Failed to export attendance');
      console.error(error);
    }
  };

  return (
    <div>
      <Header />
      <Container fluid className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <Button
              variant="link"
              className="text-decoration-none mb-2 p-0"
              onClick={() => navigate('/admin')}
            >
              <FaArrowLeft className="me-2" /> Back to Dashboard
            </Button>
            <h2 className="mb-0">📊 Attendance History</h2>
            <p className="text-muted">View attendance history for students. Click on any student to see detailed calendar view.</p>
          </div>
        </div>

        <Row>
          <Col lg={12}>
            {/* Filters Card */}
            <Card className="mb-4 shadow-sm">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">🎯 Select Batch</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Select Batch</Form.Label>
                      <Form.Select
                        value={selectedBatch}
                        onChange={handleBatchChange}
                        disabled={loadingBatches}
                      >
                        <option value="">-- Select a Batch --</option>
                        {batches.map(batch => (
                          <option key={batch.id} value={batch.id}>
                            {batch.name} ({batch.status})
                          </option>
                        ))}
                      </Form.Select>
                      {loadingBatches && (
                        <div className="mt-2">
                          <Spinner animation="border" size="sm" /> Loading batches...
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={3} className="d-flex align-items-end">
                    <Button
                      variant="info"
                      onClick={handleExport}
                      disabled={!selectedBatch || students.length === 0}
                      className="w-100"
                    >
                      <FaDownload className="me-2" />
                      Export to Excel
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Students Table Card */}
            <Card className="shadow-sm">
              <Card.Header className="bg-secondary text-white">
                <h5 className="mb-0">
                  <FaUserGraduate className="me-2" />
                  Students List
                  {students.length > 0 && (
                    <Badge bg="light" text="dark" className="ms-2">
                      {students.length} Students
                    </Badge>
                  )}
                </h5>
              </Card.Header>
              <Card.Body>
                {loading && (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Loading students...</p>
                  </div>
                )}

                {!loading && students.length === 0 && selectedBatch && (
                  <Alert variant="warning" className="text-center">
                    No students found in this batch.
                  </Alert>
                )}

                {!loading && students.length === 0 && !selectedBatch && (
                  <Alert variant="info" className="text-center">
                    Please select a batch to view students.
                  </Alert>
                )}

                {!loading && students.length > 0 && (
                  <div className="table-responsive">
                    <Table hover className="attendance-table">
                      <thead className="table-dark">
                        <tr>
                          <th>Student Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Joined Date</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map(student => (
                          <tr 
                            key={student.id}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleStudentClick(student)}
                          >
                            <td>
                              <strong className="text-primary">
                                <FaUserGraduate className="me-2" />
                                {student.name}
                              </strong>
                            </td>
                            <td>{student.email}</td>
                            <td>{student.phone || 'N/A'}</td>
                            <td>{student.joined_date || 'N/A'}</td>
                            <td className="text-center">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStudentClick(student);
                                }}
                              >
                                <FaCalendarAlt className="me-1" />
                                View Calendar
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                {!loading && students.length > 0 && (
                  <div className="mt-3 text-muted small">
                    <FaEye className="me-1" />
                    Tip: Click on any student row or the "View Calendar" button to see attendance history with calendar view.
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {showModal && selectedStudent && (
        <StudentHistoryModal
          student={selectedStudent}
          batchId={selectedBatch}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default AttendanceHistory;